import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { Badge } from '@shared/ui';

/**
 * Course Detail Page (Public View)
 *
 * Route: /:locale/courses/:courseId
 * Public page showing course details and enrollment options
 */
export const Route = createFileRoute('/$locale/courses/$courseId')({
  component: CourseDetail,
});

// Course configuration
const COURSE_CONFIG = {
  'wave-1': {
    titleKey: 'courses.wave1.title' as const,
    descriptionKey: 'courses.wave1.description' as const,
    levelKey: 'courses.levels.beginner' as const,
    duration: '3 days',
    modules: ['Breathing Fundamentals', 'Equalization Basics', 'Pool Sessions', 'Open Water Dives'],
  },
  'wave-2': {
    titleKey: 'courses.wave2.title' as const,
    descriptionKey: 'courses.wave2.description' as const,
    levelKey: 'courses.levels.intermediate' as const,
    duration: '4 days',
    modules: ['Advanced Breathing', 'Deep Equalization', 'Technique Refinement', 'Deeper Dives'],
  },
  'wave-3': {
    titleKey: 'courses.wave3.title' as const,
    descriptionKey: 'courses.wave3.description' as const,
    levelKey: 'courses.levels.advanced' as const,
    duration: '5 days',
    modules: ['Expert Techniques', 'Mental Preparation', 'Depth Training', 'Competition Prep'],
  },
} as const;

function CourseDetail() {
  const { locale, courseId } = Route.useParams();
  const { t } = useTranslation();

  // Check if courseId is a valid key in COURSE_CONFIG
  const isValidCourse = courseId in COURSE_CONFIG;
  const courseConfig = isValidCourse ? COURSE_CONFIG[courseId as keyof typeof COURSE_CONFIG] : null;

  if (!courseConfig) {
    return (
      <>
        <title>{`${t('courses.notFound')} | ${t('metadata.title')}`}</title>
        <div className="flex min-h-screen items-center justify-center bg-background">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground">{t('courses.notFound')}</h1>
            <p className="mt-2 text-muted-foreground">{t('courses.notFoundDescription')}</p>
            <Link
              to="/$locale/courses"
              params={{ locale }}
              className="mt-4 inline-block text-primary hover:underline"
            >
              {t('courses.backToCourses')}
            </Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <title>{`${t(courseConfig.titleKey)} | ${t('metadata.title')}`}</title>
      <meta name="description" content={t(courseConfig.descriptionKey)} />

      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="border-b border-border bg-card">
          <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
            <nav className="mb-4 text-sm">
              <Link
                to="/$locale"
                params={{ locale }}
                className="text-muted-foreground hover:text-foreground"
              >
                {t('navigation.home')}
              </Link>
              <span className="mx-2 text-muted-foreground">/</span>
              <Link
                to="/$locale/courses"
                params={{ locale }}
                className="text-muted-foreground hover:text-foreground"
              >
                {t('navigation.courses')}
              </Link>
              <span className="mx-2 text-muted-foreground">/</span>
              <span className="text-foreground">{t(courseConfig.titleKey)}</span>
            </nav>

            <div className="flex items-center gap-3">
              <Badge size="md">{t(courseConfig.levelKey)}</Badge>
              <span className="text-sm text-muted-foreground">{courseConfig.duration}</span>
            </div>

            <h1 className="mt-4 text-3xl font-bold tracking-tight text-foreground">
              {t(courseConfig.titleKey)}
            </h1>
            <p className="mt-2 text-lg text-muted-foreground">{t(courseConfig.descriptionKey)}</p>
          </div>
        </header>

        {/* Content */}
        <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="grid gap-8 lg:grid-cols-3">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Course Image Placeholder */}
              <div className="mb-8 aspect-video rounded-lg bg-muted" />

              {/* Course Modules */}
              <section>
                <h2 className="mb-4 text-xl font-semibold text-foreground">
                  {t('courses.modules')}
                </h2>
                <div className="space-y-3">
                  {courseConfig.modules.map((moduleTitle, index) => (
                    <div
                      key={moduleTitle}
                      className="flex items-center gap-3 rounded-lg border border-border bg-card p-4"
                    >
                      <div className="flex size-8 items-center justify-center rounded-full bg-primary/10 text-sm font-medium text-primary">
                        {index + 1}
                      </div>
                      <span className="text-card-foreground">{moduleTitle}</span>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-8 rounded-lg border border-border bg-card p-6 shadow-sm">
                <h3 className="mb-4 text-lg font-semibold text-card-foreground">
                  {t('courses.enrollTitle')}
                </h3>

                <div className="mb-6 space-y-2 text-sm text-muted-foreground">
                  <div className="flex justify-between">
                    <span>{t('courses.level')}</span>
                    <span className="font-medium text-foreground">{t(courseConfig.levelKey)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('courses.duration')}</span>
                    <span className="font-medium text-foreground">{courseConfig.duration}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('courses.modulesCount')}</span>
                    <span className="font-medium text-foreground">
                      {courseConfig.modules.length}
                    </span>
                  </div>
                </div>

                <button
                  type="button"
                  className="w-full rounded-lg bg-primary px-4 py-3 font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                >
                  {t('courses.enrollNow')}
                </button>

                <p className="mt-4 text-center text-xs text-muted-foreground">
                  {t('courses.enrollComingSoon')}
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}
